import * as THREE from 'three';
import type { CollisionWorld } from '../world/CollisionWorld';

export const CRATE_FRICTION = 6.0;
export const CRATE_GRAVITY = -20.0;
const ARENA_BOUND = 28;
const LEAN_K = 0.06;
// Batas miring: muat atan(lereng ramp paling curam 5/7 ~= 0.62 rad),
// supaya di ramp curam pun badan benar-benar selaras permukaan,
// bukan cuma setengah (sebelumnya kejepit 0.3 rad ~= 17 derajat).
const LEAN_MAX = 0.65;
const LEAN_STIFF = 90;
const LEAN_DAMP = 9;
const BOUNCE_MIN_SPEED = 4;
const BOUNCE_KEEP = 0.22;
// Pegas yaw: sisi tempel menoleh ke penarik. Berat -> lambat.
const YAW_STIFF = 50;
const YAW_DAMP_RATIO = 0.85;

export class Pushable {
  readonly pos = new THREE.Vector3();
  readonly vel = new THREE.Vector3();
  readonly halfX: number;
  readonly halfZ: number;
  readonly height: number;
  /** Massa ~ volume: mengatur kelambatan ikut, kecepatan max, dan sodokan. */
  readonly mass: number;
  readonly mesh: THREE.Mesh;
  private leanX = 0;
  private leanZ = 0;
  private leanVX = 0;
  private leanVZ = 0;
  /** Yaw badan (rad): sisi yang ditempel menoleh mengikuti arah tarikan.
   *  Visual saja (collision tetap AABB); rotasi sekitar Y tak mengubah
   *  tinggi sudut jadi kontak tanah tetap 1:1. */
  private yaw = 0;
  private yawV = 0;
  /** Target yaw (rad, konvensi atan2(x, z)) atau null bila bebas. */
  yawTarget: number | null = null;
  /** Jangkar grab dalam frame LOKAL badan crate (relatif pusat mesh).
   *  Dikunci sekali saat grab mulai; titik + normal dunia dihitung ulang
   *  dari lean saat ini -> tempelan selalu rata menempel permukaan,
   *  tidak miring keluar / melayang saat crate miring & bergoyang. */
  private anchorLocal: THREE.Vector3 | null = null;
  private anchorNormalLocal = new THREE.Vector3(0, 0, 1);
  private tmpQ = new THREE.Quaternion();
  private tmpE = new THREE.Euler();
  private tmpM = new THREE.Matrix4();
  /** Tilt tanah di bawah crate (sampel terakhir): untuk linearisasi bidang. */
  private groundTiltX = 0;
  private groundTiltZ = 0;

  constructor(x: number, y: number, z: number, size = 1, height = 1.2, color = 0xd08770) {
    this.pos.set(x, y, z);
    this.halfX = size / 2;
    this.halfZ = size / 2;
    this.height = height;
    this.mass = size * size * height;
    const geo = new THREE.BoxGeometry(size, height, size);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.7 });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    // Penanda occluder untuk fade lingkaran AIM (AimFade).
    this.mesh.userData.occluder = true;
    // Urutan YXZ: leanX/Z selalu pada sumbu DUNIA (selaras lereng),
    // yaw pada sumbu Y dunia. Lihat syncMesh.
    this.mesh.rotation.order = 'YXZ';
    this.syncMesh();
  }

  /** Kunci titik tempel saat grab mulai (disimpan dlm frame lokal). */
  lockAnchor(worldPoint: THREE.Vector3, worldNormal: THREE.Vector3): void {
    if (!this.anchorLocal) this.anchorLocal = new THREE.Vector3();
    const q = this.bodyQuat(this.tmpQ).invert();
    this.anchorLocal.copy(worldPoint).sub(this.mesh.position).applyQuaternion(q);
    this.anchorNormalLocal.copy(worldNormal).applyQuaternion(q);
    if (this.anchorNormalLocal.lengthSq() < 1e-6) this.anchorNormalLocal.set(0, 0, 1);
    this.anchorNormalLocal.normalize();
  }

  /** Titik + normal tempel dunia mengikuti rotasi badan saat ini. */
  anchorWorld(outPoint: THREE.Vector3, outNormal: THREE.Vector3): void {
    const q = this.bodyQuat(this.tmpQ);
    if (this.anchorLocal) {
      outPoint.copy(this.anchorLocal).applyQuaternion(q).add(this.mesh.position);
      outNormal.copy(this.anchorNormalLocal).applyQuaternion(q);
    } else {
      outPoint.copy(this.pos);
      outNormal.set(0, 0, 1);
    }
  }

  clearAnchor(): void {
    this.anchorLocal = null;
  }

  dispose(): void {
    this.mesh.traverse((o) => {
      if (o instanceof THREE.Mesh && o !== this.mesh) {
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      }
    });
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }

  /** Sudut sisi tempel dlm frame lokal (atan2(x, z)) atau null. */
  anchorFaceAngle(): number | null {
    if (!this.anchorLocal) return null;
    return Math.atan2(this.anchorNormalLocal.x, this.anchorNormalLocal.z);
  }

  private bodyQuat(out: THREE.Quaternion): THREE.Quaternion {
    // Sama persis dgn rotasi visual di syncMesh: Euler(leanX, yaw, leanZ)
    // order YXZ.
    return out.setFromEuler(this.tmpE.set(this.leanX, this.yaw, this.leanZ, 'YXZ'));
  }

  syncMesh(): void {
    // Dudukan 4-sudut terhadap BIDANG tanah (linearisasi tilt di sekitar
    // pos): tinggi mesh = max supaya sudut bawah TERENDAH tepat menyentuh
    // bidang. Di ramp -> muka bawah KOPLANAR dgn lereng (duduk rata
    // sejajar, bukan jinjit di edge hilir + melayang). Di datar/box
    // (tilt 0) -> identik rumus angkat lama. Yaw ikut dihitung (R penuh)
    // jadi menoleh di lereng pun duduk di sudut terendah, tak tembus.
    // Collision (kaki AABB) tetap 1:1 dengan titik kontak texture.
    this.tmpM.makeRotationFromEuler(this.tmpE.set(this.leanX, this.yaw, this.leanZ, 'YXZ'));
    const e = this.tmpM.elements;
    const R00 = e[0];
    const R10 = e[1];
    const R20 = e[2];
    const R01 = e[4];
    const R11 = e[5];
    const R21 = e[6];
    const R02 = e[8];
    const R12 = e[9];
    const R22 = e[10];
    const hx = this.halfX;
    const hz = this.halfZ;
    const hh = this.height / 2;
    let seat = this.pos.y + hh;
    for (let k = 0; k < 4; k++) {
      const lx = (k & 1) === 0 ? -hx : hx;
      const lz = (k & 2) === 0 ? -hz : hz;
      // Offset horizontal sudut: R01/R21 ikut (pusat muka bawah bergeser
      // horizontal saat miring — tanpanya dudukan meleset ~0.1 di ramp).
      const ox = R00 * lx - R01 * hh + R02 * lz;
      const oz = R20 * lx - R21 * hh + R22 * lz;
      const g = this.pos.y + this.groundTiltX * ox + this.groundTiltZ * oz;
      const support = g - (R10 * lx - R11 * hh + R12 * lz);
      if (support > seat) seat = support;
    }
    this.mesh.position.set(this.pos.x, seat, this.pos.z);
    // Yaw dunia + lean dunia (order YXZ): lean tetap selaras lereng
    // walau badan sudah menoleh.
    this.mesh.rotation.set(this.leanX, this.yaw, this.leanZ);
  }

  integrate(delta: number, world: CollisionWorld): void {
    const f = Math.exp(-CRATE_FRICTION * delta);
    this.vel.x *= f;
    this.vel.z *= f;
    if (Math.hypot(this.vel.x, this.vel.z) < 0.05) {
      this.vel.x = 0;
      this.vel.z = 0;
    }

    // Resolve horizontal DULU (sebelum ground-snap), supaya crate
    // terdorong ke samping, bukan teleport ke atas box.
    const r = world.resolveBox(this.pos, this.halfX, this.halfZ, this.height);
    if (r.x !== this.pos.x || r.z !== this.pos.z) {
      this.pos.x = r.x;
      this.pos.z = r.z;
      this.vel.x *= 0.3;
      this.vel.z *= 0.3;
    }

    const s = world.sampleGroundHeight(this.pos.x, this.pos.z, this.pos.y + this.height);
    const groundY = s.valid ? s.height : 0;
    this.groundTiltX = s.tiltX;
    this.groundTiltZ = s.tiltZ;
    this.vel.y += CRATE_GRAVITY * delta;
    this.pos.x += this.vel.x * delta;
    this.pos.z += this.vel.z * delta;
    this.pos.y += this.vel.y * delta;
    if (this.pos.y <= groundY) {
      this.pos.y = groundY;
      if (this.vel.y < -BOUNCE_MIN_SPEED) {
        const impact = Math.min(1, -this.vel.y / 12);
        this.vel.y = -this.vel.y * BOUNCE_KEEP;
        this.leanVX += this.vel.z * impact;
        this.leanVZ += -this.vel.x * impact;
      } else if (this.vel.y < 0) {
        this.vel.y = 0;
      }
    }

    // Target miring: selaras lereng (atan, bukan linear: akurat sampai
    // ramp curam) + condong ikut gerak, via pegas underdamped supaya
    // bergoyang natural saat start/stop/mendarat.
    const tx = THREE.MathUtils.clamp(
      -Math.atan(s.tiltZ) + this.vel.z * LEAN_K,
      -LEAN_MAX,
      LEAN_MAX,
    );
    const tz = THREE.MathUtils.clamp(
      Math.atan(s.tiltX) - this.vel.x * LEAN_K,
      -LEAN_MAX,
      LEAN_MAX,
    );
    this.leanVX += ((tx - this.leanX) * LEAN_STIFF - this.leanVX * LEAN_DAMP) * delta;
    this.leanVZ += ((tz - this.leanZ) * LEAN_STIFF - this.leanVZ * LEAN_DAMP) * delta;
    this.leanX += this.leanVX * delta;
    this.leanZ += this.leanVZ * delta;

    // Yaw menoleh ke target (sisi tempel menghadap penarik). Busur
    // terpendek; berat -> pegas lebih lembek (menoleh lambat).
    // PENGECUALIAN lereng: kotak yang menoleh di bidang miring tak bisa
    // duduk rata (bertumpu satu sudut + melayang) -> putaran dilepas
    // kembali ke kelipatan 2π terdekat supaya koplanar dgn lereng.
    // Menoleh bebas hanya di tanah datar/dangkal.
    let yawGoal = this.yawTarget;
    if (Math.hypot(s.tiltX, s.tiltZ) > 0.08) {
      yawGoal = Math.round(this.yaw / (Math.PI * 2)) * Math.PI * 2;
    }
    if (yawGoal !== null) {
      const diff = Math.atan2(
        Math.sin(yawGoal - this.yaw),
        Math.cos(yawGoal - this.yaw),
      );
      const stiff = YAW_STIFF / this.mass;
      const damp = 2 * Math.sqrt(stiff) * YAW_DAMP_RATIO;
      this.yawV += (stiff * diff - damp * this.yawV) * delta;
      this.yaw += this.yawV * delta;
    } else {
      this.yawV *= Math.exp(-6 * delta);
      this.yaw += this.yawV * delta;
    }

    this.pos.x = THREE.MathUtils.clamp(this.pos.x, -ARENA_BOUND, ARENA_BOUND);
    this.pos.z = THREE.MathUtils.clamp(this.pos.z, -ARENA_BOUND, ARENA_BOUND);
    this.syncMesh();
  }
}

/** Crate terdekat di depan dalam jangkauan, atau null. */
export function findGrabTarget(
  playerPos: THREE.Vector3,
  forward: THREE.Vector3,
  pushables: Pushable[],
  range: number,
): Pushable | null {
  let best: Pushable | null = null;
  let bestDist = Infinity;
  for (const c of pushables) {
    const dx = c.pos.x - playerPos.x;
    const dz = c.pos.z - playerPos.z;
    const dist = Math.hypot(dx, dz);
    if (dist > range || dist < 1e-4) continue;
    if ((dx * forward.x + dz * forward.z) / dist < 0.2) continue;
    if (dist < bestDist) {
      best = c;
      bestDist = dist;
    }
  }
  return best;
}

/** Pisahkan dua crate yang bertumpuk (bagi rata sumbu penetrasi terkecil). */
export function separateCrates(a: Pushable, b: Pushable): void {
  if (a.pos.y >= b.pos.y + b.height || b.pos.y >= a.pos.y + a.height) return;
  const ox = a.halfX + b.halfX - Math.abs(a.pos.x - b.pos.x);
  const oz = a.halfZ + b.halfZ - Math.abs(a.pos.z - b.pos.z);
  if (ox <= 0 || oz <= 0) return;
  if (ox < oz) {
    const s = a.pos.x === b.pos.x ? 1 : Math.sign(a.pos.x - b.pos.x);
    a.pos.x += (s * ox) / 2;
    b.pos.x -= (s * ox) / 2;
  } else {
    const s = a.pos.z === b.pos.z ? 1 : Math.sign(a.pos.z - b.pos.z);
    a.pos.z += (s * oz) / 2;
    b.pos.z -= (s * oz) / 2;
  }
  a.syncMesh();
  b.syncMesh();
}
